import {
  GameModes,
  parseQuery,
  RoundStatus,
  applicationStore,
  notificationStore,
  DefaultQueryParams,
  parseNumberByPrecision,
  loadingStore,
} from '@pascal/pascal-core';
import {
  betStore,
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameResultStore,
  gameConfigStore,
  profileAvatarStore,
} from '@pascal/pascal-core/shared/core/store';
import { nanoid } from 'nanoid';

import { worker } from '../index';
import { liveConnection } from '../core';
import { circlesArr } from '../enums/circlesArr';
import { gameColorsCodes } from '../enums/gameColors';
import { gameLiveConnection } from './GameConnection';
import { betTypesStore } from '../store/betTypesStore';
import { RESULTS_LIST_LENGTH } from '../enums/results';
import { soundService, soundTypes } from './soundService';
import { doBetStore, gameStore, roundTimeStore, wheelStore } from '../store';
import { parseDataFromLiveBets } from '../helpers';

let timeOutId;

class GameService {
  wheelTimeout = null;

  roundFinTimeoutId = null;

  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    avatarId,
    precision,
    roundInfo,
    currentBets,
    currencyCode,
    gameConfiguration,
  }) {
    const { Started, Finished, OnFinish } = RoundStatus;
    const { status, second, duration, animationDuration, betsClosedDuration, roundId } = roundInfo;
    const { [DefaultQueryParams.Mode]: mode } = parseQuery(window.location.search);

    gameStore.setGameAnimationDuration((animationDuration - betsClosedDuration) * 1000);

    if (this.wheelTimeout) {
      clearTimeout(this.wheelTimeout);
    }

    if (this.roundFinTimeoutId) {
      clearTimeout(this.roundFinTimeoutId);
    }

    gameStore.setGameState({
      status,
      isInitialLoaded: true,
    });

    if (currentBets && currentBets?.length) {
      currentBets.forEach((bet) => {
        if (+bet.userId === +userInfoStore.userId && status === Finished) {
          doBetStore.updatedReBetData({ ...bet, roundId, betData: bet.betData.markets[0] });
        }

        if (bet.betData.markets.length > 1) {
          bet.betData.markets.forEach((market) => {
            betStore.updateLiveBets(
              { ...bet, id: market.id, betData: { markets: [market] }, amount: market.amount },
              false
            );
            betTypesStore.updateBetTypesActiveBets(market.market);
          });
        } else {
          betTypesStore.updateBetTypesActiveBets(bet.betData.markets[0].market);
          betStore.updateLiveBets(bet, false);
        }
      });
    }

    let current;
    let next;

    const wheelResults = results
      .filter((el) => {
        if (el.status === RoundStatus.Finished || el.status === RoundStatus.OnFinish) {
          return true;
        }
        if (el.status === RoundStatus.OnFinish) {
          next = el;
        }
        return false;
      })
      .map((el, index) => {
        if (index === 0) {
          const currentIndex = circlesArr.find(
            (item) => item.color === gameColorsCodes[el.roundData]
          ).index;
          current = {
            color: el.roundData,
            index: currentIndex,
          };
          return {
            color: gameColorsCodes[el.roundData],
            id: nanoid(),
            index: currentIndex,
          };
        }
        return {
          color: gameColorsCodes[el.roundData],
          id: nanoid(),
        };
      });
    wheelStore.setWheelResultsHistory(wheelResults);

    if (status === Started) {
      const isBetClosed = second <= 0;
      if (isBetClosed) {
        soundService.play(soundTypes.wheel);
        doBetStore.updateIsDisabledBetSection(true);
        gameStore.setIsBetsClosed(true);
      }
      doBetStore.setWinAmount('');
    } else if (status === Finished || status === OnFinish) {
      //
    }

    roundInfoStore.setDuration(duration);
    betTypesStore.setBetTypes(gameConfiguration.markets);

    if (!next) {
      wheelStore.setWheelTimerToggle(true);
    }

    if (mode === GameModes.Fun) {
      applicationStore.setIsDemo(true);
    }

    gameResultStore.setGameResults(results);
    profileAvatarStore.updateInitialAvatarId(avatarId);

    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);

    if (RoundStatus.Started === status && second > 0) {
      gameStore.setTimerDuration(roundInfo.second);
      const time = Math.floor(Math.abs(roundInfo.second));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
      if (time === 0) {
        roundTimeStore.setTime(null);
      }
    } else {
      gameStore.setIsBetsClosed(true);
      this.sendWorker();
      roundTimeStore.setTime(null);

      if (RoundStatus.Started === status && second < 0) {
        gameStore.setIsRoundFinishedHandled(false);
      } else if (
        (status === RoundStatus.OnFinish || status === RoundStatus.Finished) &&
        second < 0
      ) {
        gameStore.setIsRoundFinishedHandled(true);
        roundTimeStore.setPassedSeconds(second + betsClosedDuration);
        soundService.play(soundTypes.wheel, -roundTimeStore.passedSeconds);
        this.roundFinTimeoutId = setTimeout(() => {
          wheelStore.setWheelResult(current);
          let currentTotalWinAmount;
          if (currentBets && currentBets.length) {
            currentTotalWinAmount = currentBets
              .filter((item) => item?.userId === userInfoStore.userId && item.winAmount)
              .reduce(
                (acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2),
                0
              );
          }
          doBetStore.setWinAmount(currentTotalWinAmount);
        }, gameStore.gameAnimationDuration - -roundTimeStore.passedSeconds * 1000);

        gameStore.setTimerDuration(roundInfo.duration);
      }
    }

    document.documentElement.style.setProperty('--wheelCurrentIndex', `${-wheelResults[0].index}`);
    document.documentElement.style.setProperty('--wheelNextIndex', `${-wheelResults[0].index}`);

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);

    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    if (Number(userId) === Number(+data.userId)) {
      doBetStore.handleDoBetResult(data);
      soundService.playBetAccepted();
      doBetStore.updateIsDisabledBetSection(false);
    }

    if (data.betData.markets && data.betData.markets.length > 1) {
      data.betData.markets.forEach((el) => {
        betTypesStore.updateBetTypesActiveBets(el.market);
        betStore.updateLiveBets({
          ...el,
          betData: { markets: [el] },
          userId: data.userId,
          id: el.id,
          freeBetId: data.freeBetId,
          amount: el.amount,
          playerExternalId: data.playerExternalId,
        });
      });
    } else {
      betTypesStore.updateBetTypesActiveBets(data.betData.markets[0].market);
      betStore.updateLiveBets(data, false);
    }
  }

  handleDoBetResultValidation(data) {
    const { responseCode } = data;
    if (doBetStore.betData.isDisabled) {
      doBetStore.updateIsDisabledBetSection(false);
    }
    doBetStore.setIsLoadingBetButton(false);
    doBetStore.setIsLoadingReBetButton(false);

    notificationStore.setNotificationType({ responseCode });
  }

  handleRoundStart({ data: { startedRound, results } }) {
    const { roundId } = startedRound;
    roundTimeStore.resetRoundTimeData();
    wheelStore.setWheelTimerToggle(true);
    gameStore.setIsRoundFinishedHandled(false);
    wheelStore.setWheelResult(null);
    gameStore.setIsBetsClosed(false);
    doBetStore.setWinAmount('');
    betTypesStore.resetBetTypesActiveBets();

    this.sendWorker(startedRound.duration || roundInfoStore.duration);

    gameStore.setGameState({
      status: RoundStatus.Started,
      isInitialLoaded: false,
    });

    gameResultStore.setGameResults(results);
    betStore.setLiveBets([]);

    roundInfoStore.setRoundId(roundId);
  }

  handleRoundFin({ data }) {
    const { bets, finishedRound } = data;
    const { userId } = userInfoStore;
    const { gameAnimationDuration } = gameStore;
    // const { isDemo } = applicationStore;
    const { repliedBetData } = betStore;
    const { wheelResultsHistory } = wheelStore;
    // const {
    //   limits: { maxWin },
    // } = gameConfigStore;

    // const { Lose, Win } = BetStatus;
    // let demoBets = [];
    let replayColorIndex;

    gameStore.setIsRoundFinishedHandled(true);

    if (repliedBetData) {
      replayColorIndex = circlesArr.find(
        (item) => item.color === gameColorsCodes[repliedBetData.roundData]
      ).index;
    }

    document.documentElement.style.setProperty(
      '--wheelNextIndex',
      `${-(repliedBetData ? replayColorIndex : finishedRound.roundData.index)}`
    );

    let resultsForSlice = [
      {
        ...finishedRound.roundData,
        color:
          gameColorsCodes[
            repliedBetData ? repliedBetData?.roundData : finishedRound.roundData.color
          ],
        anim: true,
        id: nanoid(),
      },
      ...wheelResultsHistory,
    ];

    if (resultsForSlice.length >= RESULTS_LIST_LENGTH) {
      resultsForSlice = resultsForSlice.slice(0, RESULTS_LIST_LENGTH);
    }

    if (this.wheelTimeout) {
      clearTimeout(this.wheelTimeout);
    }

    this.wheelTimeout = setTimeout(() => {
      wheelStore.setWheelResult(
        repliedBetData
          ? { index: repliedBetData.additionalParams, color: repliedBetData?.roundData }
          : finishedRound.roundData
      );
      wheelStore.setWheelResultsHistory(resultsForSlice);

      clearTimeout(this.wheelTimeout);
    }, gameAnimationDuration);

    if (bets.length) {
      if (this.roundFinTimeoutId) {
        clearTimeout(this.roundFinTimeoutId);
      }

      this.roundFinTimeoutId = setTimeout(() => {
        let myWinAmount = 0;
        const myBets = bets.filter((bet) => +bet.userId === +userId);

        if (myBets.length) {
          doBetStore.detectAndSetReBetData(myBets);

          myWinAmount = myBets.reduce(
            (acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2),
            0
          );
        }

        if (myWinAmount) {
          soundService.playWin();
          doBetStore.setWinAmount(myWinAmount);
        }

        const finishLiveBets = parseDataFromLiveBets(bets);
        betStore.setLiveBets(finishLiveBets);
      }, gameAnimationDuration);
    }

    doBetStore.updateTotalBetsAmount(0, true);
  }

  handleBetsClosed() {
    gameStore.setIsBetsClosed(true);
    setTimeout(() => soundService.play(soundTypes.wheel), 900);

    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });
    doBetStore.setIsLoadingBetButton(false);
    doBetStore.setIsLoadingReBetButton(false);
    doBetStore.updateIsDisabledBetSection(true);
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
